import * as i0 from '@angular/core';
import { InjectionToken, makeEnvironmentProviders, inject, Injectable, Pipe, NgModule } from '@angular/core';
import { Router } from '@angular/router';
import { _inArray } from '@fabric/components/private/utilities';

/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/*
 * Copyright (c) 2022-2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
/**
 * Injection Token for providing the nav items.
 */
const APP_NAV_ITEMS = new InjectionToken('nav.items');
/**
 * This function provides nav items to standalone applications
 * @param navItems Icon theme you'd like to apply globally
 * @returns Environment provider for the icon theme injection token
 */
function provideNavItems(navItems) {
    return makeEnvironmentProviders([{ provide: APP_NAV_ITEMS, useValue: navItems }]);
}

/*
 * Copyright (c) 2022-2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
class FabNavService {
    constructor() {
        /** Cache for nav item ancestry */
        this.ancestryCache = {};
        /** Cache for mapping nav item state to its authorized children */
        this.authorizedChildrenCache = {};
        /** Cache for top-level authorized nav items */
        this.authorizedTopLevelNavItemsCache = [];
        /** State structure for managing and access nav item config provided by users */
        this.flattenedNavItems = [];
        /** Cache for stable access to route match options that are shared across nav items */
        this.routeMatchOptionsCache = {};
        /** Angular Router */
        this.router = inject(Router);
        /** List of user authorities to filter the nav items by */
        this.userAuthorities = [];
    }
    /**
     * Using the default route match options and the router, return the active
     * nav item.
     * A nav item is considered active if it resolves as active, when using
     * the merged route match options (its own and the default) along with its
     * route, fragment, and query params.
     * (https://angular.io/api/router/Router#isActive)
     * @param defaultRouteMatchOptions - Default route match options to use for the evaluation
     * @returns The active nav item or null if none found
     */
    getActiveNavItem(defaultRouteMatchOptions) {
        return this.getAuthorizedNavItems().find((navItem) => {
            const routeMatchOptions = this.getRouteMatchOptions(navItem, defaultRouteMatchOptions);
            return this.router.isActive(navItem.urlTree, routeMatchOptions);
        });
    }
    /**
     * Get the array of nav items that are authorized for the current user
     * @returns Array of nav items that are authorized for the current user
     */
    getAuthorizedNavItems() {
        return this.flattenedNavItems.filter((navItem) => navItem.hasAccess);
    }
    /**
     * Given a nav item id, return the array of child nav items
     * @param navItem id of the parent nav item
     * @returns Array of child nav items
     */
    getAuthorizedChildren(navItem) {
        // If we don't already have the result cached, then create a new array of authorized children
        // and add it to the cache
        if (!this.authorizedChildrenCache[navItem.id]) {
            this.authorizedChildrenCache[navItem.id] = navItem.children
                .map((childId) => this.getNavItem(childId))
                .filter((child) => child && child.hasAccess);
        }
        return this.authorizedChildrenCache[navItem.id];
    }
    /**
     * This function returns a `NavItemState` in the state structure by id
     * @param id - Index of the nav item to retrieve state for
     * @returns corresponding state item at this index
     */
    getNavItem(id) {
        if (id < this.flattenedNavItems.length) {
            return this.flattenedNavItems[id];
        }
        else {
            return null;
        }
    }
    /**
     * Given a nav item, return an array of all of its parents, starting with the top level parent
     * @param navItem - nav item you want to create the ancestry from
     * @returns array of nav items that are the ancestry of the given nav item
     */
    getNavItemAncestry(navItem) {
        // If this is already in the cache, then return it from cache
        if (this.ancestryCache[navItem.id]) {
            return this.ancestryCache[navItem.id];
        }
        // Otherwise, build a new ancestry array and add it to the cache
        const ancestry = this.createNavItemAncestry(navItem);
        this.ancestryCache[navItem.id] = ancestry;
        return ancestry;
    }
    /**
     * Returns the filtered nav item state structure
     * @returns nav item state structure
     */
    getNavItemState() {
        return this.flattenedNavItems;
    }
    /**
     * Create the nav item state structure from a new set of config objects, and filter the resulting structure
     * by user authorities.
     * @param navItems - Array of `FabNavItem`s to use for the state structure
     */
    setNavItems(navItems) {
        this.flattenedNavItems = [];
        this.flattenNavItems(navItems);
        this.updateNavItemAccess();
        this.clearCache();
    }
    /**
     * Set the user's authorities, and filter the nav items by the new authorities.
     * @param userAuthorities -
     */
    setUserAuthorities(userAuthorities) {
        this.userAuthorities = userAuthorities;
        this.updateNavItemAccess();
        this.clearCache();
    }
    /**
     * Returns the route match options for the given partial route match options.  Uses cache to avoid creating redundant
     * objects. The cache key is the nav item state id
     * @param navItem - nav item state to use for route match options
     * @param defaultRouteMatchOptions - Default route match options
     * @returns combined route match options
     */
    getRouteMatchOptions(navItem, defaultRouteMatchOptions) {
        // Use the state id for the cache key
        const cacheKey = navItem.id;
        if (this.routeMatchOptionsCache[cacheKey]) {
            return this.routeMatchOptionsCache[cacheKey];
        }
        else if (navItem.config.routeMatchOptions) {
            // If the nav item has route match options, merge them with the default options
            const routeMatchOptions = {
                ...defaultRouteMatchOptions,
                ...navItem.config.routeMatchOptions,
            };
            this.routeMatchOptionsCache[cacheKey] = routeMatchOptions;
            return routeMatchOptions;
        }
        else {
            this.routeMatchOptionsCache[cacheKey] = defaultRouteMatchOptions;
            return defaultRouteMatchOptions;
        }
    }
    /**
     * Gets the array of top level nav items, filtered by access
     * @returns Array of top level nav items, filtered by access
     */
    getAuthorizedTopLevelNavItems() {
        // If this has not already been cached, then create a new array and cache the results
        if (!this.authorizedTopLevelNavItemsCache.length) {
            this.authorizedTopLevelNavItemsCache = this.flattenedNavItems.filter((navItem) => navItem.parent === -1 && navItem.hasAccess);
        }
        return this.authorizedTopLevelNavItemsCache;
    }
    /**
     * Given a nav item, return the top level parent of that nav item
     * @param navItem - child nav item
     * @returns top level parent of the nav item
     */
    getTopLevelItemFromChild(navItem) {
        // If this is already a top level item, then bail
        if (navItem.parent === -1) {
            return navItem;
        }
        // Otherwise, recurse until we find the top level parent
        return this.getTopLevelItemFromChild(this.getNavItem(navItem.parent));
    }
    /**
     * Generate a url tree for the current config.
     * We'll use this later to detective activeness.
     * (In the future we can include query params and fragments)
     * @param navItemConfig configuration for the nav item we want a url for
     * @returns UrlTree for the nav item or undefined if no route is defined
     */
    createUrlTree(navItemConfig) {
        let urlTree;
        if (typeof navItemConfig.route !== 'undefined' && navItemConfig.route !== null) {
            urlTree = this.router.createUrlTree([navItemConfig.route]);
        }
        return urlTree;
    }
    /**
     * Clears all cache structures
     */
    clearCache() {
        this.ancestryCache = {};
        this.authorizedChildrenCache = {};
        this.authorizedTopLevelNavItemsCache = [];
        this.routeMatchOptionsCache = {};
    }
    /**
     * Given a nav item, return an array of all of its parents, starting with the top level parent
     * @param navItem - nav item you want to create the ancestry from
     * @returns array of nav items that are the ancestry of the given nav item
     */
    createNavItemAncestry(navItem) {
        // If this is already a top level item, then bail
        if (navItem.parent === -1) {
            return [navItem];
        }
        // Otherwise, recurse until we find the top level parent
        const ancestry = [];
        const parent = this.getNavItem(navItem.parent);
        if (parent) {
            ancestry.push(...this.getNavItemAncestry(parent));
        }
        ancestry.push(navItem);
        return ancestry;
    }
    /**
     * This function flattens the nav item tree structure into a simple array of state-tracking objects
     * @param navItemsConfig - Full nav item tree structure
     * @param parent - ID of the parent NavItemState
     * @returns new nav item state elements corresponding to the given nav item config array
     */
    flattenNavItems(navItemsConfig, parent = -1) {
        // Track nav item state created during this call
        const currentNavItemState = [];
        // For each FabNavItem, assign an id and add a NavItemState object to the state array
        for (const config of navItemsConfig) {
            const newId = this.flattenedNavItems.length;
            const newNavItemState = {
                id: newId,
                parent,
                children: [],
                config,
                hasAccess: true,
                urlTree: this.createUrlTree(config),
            };
            this.flattenedNavItems.push(newNavItemState);
            currentNavItemState.push(newNavItemState);
            // Recurse on subItems so that they're added to the array next
            if (config.subItems) {
                // This recursive call will return the array of nav item state for each child element.
                // We can use this to populate the current nav item's children array.
                const children = this.flattenNavItems(config.subItems, newId);
                // Build the children id array
                const childrenIds = [];
                for (const child of children) {
                    childrenIds.push(child.id);
                }
                newNavItemState.children = childrenIds;
            }
        }
        return currentNavItemState;
    }
    /**
     * Checks if the passed navItem's authority property is included in the authorities list
     * @param navItemConfig - nav item containing to compare against authorities
     * @param authorities - list of authorities to compare against navItem
     * @returns true if navItem's authority is undefined or there is a match
     */
    hasAuthority(navItemConfig, authorities) {
        const { authority } = navItemConfig;
        if (!authority) {
            return true;
        }
        return typeof authority === 'string'
            ? authorities?.includes(authority)
            : authorities && _inArray(authority, authorities);
    }
    /**
     * Updates the `hasAccess` property on each nav item state object by comparing what's in the `FabNavItem` config
     * with what's defined in the user authorities array.
     */
    updateNavItemAccess() {
        // Do a first pass to turn off access for any nav items that have an authority the user doesn't have
        for (const navItemState of this.flattenedNavItems) {
            navItemState.hasAccess = this.hasAuthority(navItemState.config, this.userAuthorities);
        }
        // Do a second pass (in reverse) to turn on access for any parents that have children with access
        for (let i = this.flattenedNavItems.length - 1; i >= 0; --i) {
            const navItemState = this.flattenedNavItems[i];
            if (navItemState.hasAccess && navItemState.children.length > 0) {
                navItemState.hasAccess = navItemState.children.some((childId) => this.flattenedNavItems[childId].hasAccess);
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: FabNavService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: FabNavService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: FabNavService, decorators: [{
            type: Injectable
        }] });

/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
class FabDecamelizePipe {
    transform(value, separator = ' ') {
        const input = value ?? '';
        return input
            .replace(/([a-z])([A-Z\d])/g, `$1${separator}$2`)
            .replace(/([A-Z]+)([A-Z][a-z\d]+)/g, `$1${separator}$2`)
            .toLowerCase();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: FabDecamelizePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.7", ngImport: i0, type: FabDecamelizePipe, isStandalone: true, name: "decamelize" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: FabDecamelizePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'decamelize',
                    standalone: true,
                }]
        }] });

/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
class FabDunsPipe {
    constructor() {
        this.dunsLength = 9;
    }
    transform(value) {
        let output = value.toString();
        while (output.length < this.dunsLength) {
            output = '0' + output;
        }
        return output;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: FabDunsPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.7", ngImport: i0, type: FabDunsPipe, isStandalone: true, name: "duns" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: FabDunsPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'duns',
                    standalone: true,
                }]
        }] });

/*
 * Copyright (c) 2022-2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
class FabPipesModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: FabPipesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.7", ngImport: i0, type: FabPipesModule, imports: [FabDecamelizePipe, FabDunsPipe], exports: [FabDecamelizePipe, FabDunsPipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: FabPipesModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: FabPipesModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [FabDecamelizePipe, FabDunsPipe],
                    exports: [FabDecamelizePipe, FabDunsPipe],
                }]
        }] });

/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
/**
 * Created by michael faith on 3/30/2020.
 */

/**
 * Generated bundle index. Do not edit.
 */

export { APP_NAV_ITEMS, FabDecamelizePipe, FabDunsPipe, FabNavService, FabPipesModule, provideNavItems };
